import { lazy } from '@loadable/component';
import NotFound from 'apps/TenantApp/screens/NotFound';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { FolderViewer_Folder_Query } from './__generated__/FolderViewer_Folder_Query.graphql';
import { SuspendWithRelativelyCenteredSpinner } from 'components/suspense/SuspendWithRelativelyCenteredSpinner';

const Folder = lazy(() => import('components/Folder'));

const FOLDER_QUERY_PARAM_NAME = 'folder';
const folderQuery = graphql`
  query FolderViewer_Folder_Query($folderId: ID!) {
    folder: node(id: $folderId) {
      ... on Folder {
        ...Folder_folder
      }
    }
  }
`;

interface FolderViewerProps {
  rootFolderId: string;
  rootFolderName: string;
}

export const FolderViewer = ({
  rootFolderId,
  rootFolderName,
}: FolderViewerProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const folderId = params.get(FOLDER_QUERY_PARAM_NAME);

  useEffect(() => {
    if (params.has(FOLDER_QUERY_PARAM_NAME)) {
      return;
    }
    params.set(FOLDER_QUERY_PARAM_NAME, rootFolderId);
    navigate({ search: params.toString() }, { replace: true });
  }, [rootFolderId, params]);

  if (!folderId) {
    return null;
  }

  return (
    <SuspendWithRelativelyCenteredSpinner>
      <UnsuspendedFolderViewer
        folderId={folderId}
        rootFolderId={rootFolderId}
        rootFolderName={rootFolderName}
      />
    </SuspendWithRelativelyCenteredSpinner>
  );
};

interface UnsuspendedFolderViewerProps {
  folderId: string;
  rootFolderId: string;
  rootFolderName: string;
}

const UnsuspendedFolderViewer = ({
  folderId,
  rootFolderId,
  rootFolderName,
}: UnsuspendedFolderViewerProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { folder } = useLazyLoadQuery<FolderViewer_Folder_Query>(folderQuery, {
    folderId,
  });

  if (!folder) {
    return <NotFound />;
  }

  return (
    <Folder
      folder={folder}
      rootFolderId={rootFolderId}
      rootFolderName={rootFolderName}
    />
  );
};

interface UseOpenFolderReturn {
  openFolder(folderId: string): void;

  openFolderTo(folderId: string): string;
}

export function useOpenFolder(): UseOpenFolderReturn {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const searchGenerator = (folderId: string): string => {
    params.set(FOLDER_QUERY_PARAM_NAME, folderId);
    return params.toString();
  };
  return {
    openFolder: (folderId: string) => {
      navigate({ search: searchGenerator(folderId) });
    },
    openFolderTo: (folderId) => `?${searchGenerator(folderId)}`,
  };
}
